"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { acknowledgeDisclaimer, hasAcknowledgedDisclaimer } from "@/lib/breach/storage"

/**
 * First-run acknowledgment shown before the intake / workspace.
 * DRAFT wording: must be reviewed and approved by counsel before public launch.
 */
export function DisclaimerGate({ children }: { children: React.ReactNode }) {
  const [ready, setReady] = useState(false)
  const [ok, setOk] = useState(false)

  useEffect(() => {
    setOk(hasAcknowledgedDisclaimer())
    setReady(true)
  }, [])

  if (!ready) return null
  if (ok) return <>{children}</>

  return (
    <div className="mx-auto max-w-2xl rounded-xl border border-border bg-card p-6 sm:p-8" role="region" aria-labelledby="gate-title">
      <h1 id="gate-title" className="text-2xl font-bold">Before you start</h1>
      <p className="mt-3 text-muted-foreground">
        This is a <strong className="text-foreground">Day-1 guide</strong> for people who don&apos;t already have an incident
        response plan. If you have a tested plan, follow it. This tool doesn&apos;t replace your lawyer, your insurer, or a
        professional incident-response firm.
      </p>
      <ul className="mt-4 list-disc space-y-2 pl-5 text-sm text-muted-foreground">
        <li>It gives general information based on NIST SP 800-61 Rev. 3. It is <strong className="text-foreground">not legal advice</strong>.</li>
        <li>Laws and deadlines vary and change. Anything shown as &ldquo;may apply&rdquo; must be confirmed with counsel.</li>
        <li>It currently covers NIST and <strong className="text-foreground">US federal</strong> rules only. State and non-US laws also apply and are not yet included.</li>
        <li>Your answers stay in <strong className="text-foreground">this browser</strong> unless you choose to sign in later. Don&apos;t enter passwords, keys, or other people&apos;s personal data.</li>
        <li>If anyone is in danger, call 911 (or your local emergency number).</li>
      </ul>
      <div className="mt-6 flex flex-wrap items-center gap-4">
        <Button
          size="lg"
          className="bg-accent-solid text-accent-solid-foreground hover:bg-accent-solid/90"
          onClick={() => { acknowledgeDisclaimer(); setOk(true) }}
        >
          I understand — continue
        </Button>
        <Link href="/disclaimer" className="text-sm underline text-muted-foreground hover:text-foreground">
          Read the full disclaimer
        </Link>
      </div>
    </div>
  )
}
